import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import { SharedModule } from '@shared/shared.module';

export interface LoginBackgroundSettings {
  image: string;
  size: string;
  positionX: number;
  positionY: number;
  overlay: number;
  blur: number;
  brightness: number;
  contrast: number;
  effect: string;
}

interface LoginBackgroundPreset {
  image: string;
  positionX: number;
  blur?: number;
}

@Component({
  selector: 'app-login-background-designer',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './login-background-designer.component.html',
  styleUrls: ['./login-background-designer.component.scss'],
})
export class LoginBackgroundDesignerComponent implements OnInit {
  readonly visible = input(false);
  readonly settingsChange = output<LoginBackgroundSettings>();

  private readonly randomPresets: LoginBackgroundPreset[] = [
    { image: 'assets/mcp-guide/hero-background.png', positionX: 50 },
    { image: 'assets/mcp-guide/hero-background.png', positionX: 0 },
    { image: 'assets/mcp-guide/hero-background.png', positionX: 100 },
    { image: 'assets/mcp-guide/hero-background2.png', positionX: 69, blur: 8 },
    { image: 'assets/mcp-guide/hero-background3.png', positionX: 0 },
    { image: 'assets/mcp-guide/hero-background4.png', positionX: 0 },
    { image: 'assets/mcp-guide/hero-background4.png', positionX: 100 },
    { image: 'assets/mcp-guide/hero-background4.png', positionX: 30 },
    { image: 'assets/mcp-guide/hero-background5.png', positionX: 27 },
    { image: 'assets/mcp-guide/hero-background5.png', positionX: 60 },
    { image: 'assets/mcp-guide/hero-background5.png', positionX: 100 },
    { image: 'assets/mcp-guide/hero-background6.png', positionX: 61 },
    { image: 'assets/mcp-guide/hero-background6.png', positionX: 5 },
    { image: 'assets/mcp-guide/hero-background6.png', positionX: 100 },
    { image: 'assets/mcp-guide/hero-background7.png', positionX: 0 },
    { image: 'assets/mcp-guide/hero-background7.png', positionX: 60 },
    { image: 'assets/mcp-guide/hero-background7.png', positionX: 100 },
    { image: 'assets/mcp-guide/hero-background8.png', positionX: 0 },
    { image: 'assets/mcp-guide/hero-background8.png', positionX: 35 },
    { image: 'assets/mcp-guide/hero-background8.png', positionX: 100 },
    { image: 'assets/mcp-guide/footer-background.png', positionX: 0 },
    { image: 'assets/mcp-guide/footer-background.png', positionX: 55 },
    { image: 'assets/mcp-guide/footer-background.png', positionX: 100 },
  ];

  readonly imageOptions: Option[] = [
    { label: 'Hero 1', value: 'assets/mcp-guide/hero-background.png' },
    { label: 'Hero 2', value: 'assets/mcp-guide/hero-background2.png' },
    { label: 'Hero 3', value: 'assets/mcp-guide/hero-background3.png' },
    { label: 'Hero 4', value: 'assets/mcp-guide/hero-background4.png' },
    { label: 'Hero 5', value: 'assets/mcp-guide/hero-background5.png' },
    { label: 'Hero 6', value: 'assets/mcp-guide/hero-background6.png' },
    { label: 'Hero 7', value: 'assets/mcp-guide/hero-background7.png' },
    { label: 'Hero 8', value: 'assets/mcp-guide/hero-background8.png' },
    { label: 'Footer', value: 'assets/mcp-guide/footer-background.png' },
  ];

  readonly sizeOptions: Option[] = [
    { label: 'Cobrir', value: 'cover' },
    { label: 'Conter', value: 'contain' },
    { label: 'Automático', value: 'auto' },
  ];

  readonly effectOptions: Option[] = [
    { label: 'Parallax', value: 'parallax' },
    { label: 'Spotlight', value: 'spotlight' },
    { label: 'Inclinação', value: 'tilt' },
    { label: 'Parallax + luz', value: 'parallax-spotlight' },
    { label: 'Desligado', value: 'none' },
  ];

  readonly selectedImage = signal<Option>(this.imageOptions[0]);
  readonly selectedSize = signal<Option>(this.sizeOptions[0]);
  readonly selectedEffect = signal<Option>(this.effectOptions[4]);
  readonly positionX = signal(50);
  readonly overlay = signal(14);
  readonly blur = signal(0);
  readonly isOpen = signal(true);

  readonly settings = computed<LoginBackgroundSettings>(() => ({
    image: this.selectedImage().value,
    size: this.selectedSize().value,
    positionX: this.positionX(),
    positionY: 44,
    overlay: this.overlay(),
    blur: this.blur(),
    brightness: 100,
    contrast: 100,
    effect: this.selectedEffect().value,
  }));

  ngOnInit(): void {
    this.applyRandomPreset();
  }

  handleImageChange(option: Option | null): void {
    if (!option) return;
    this.selectedImage.set(option);
    this.blur.set(this.getImageDefaultBlur(option.value));
    this.emitSettings();
  }

  handleSizeChange(option: Option | null): void {
    if (!option) return;
    this.selectedSize.set(option);
    this.emitSettings();
  }

  handleEffectChange(option: Option | null): void {
    if (!option) return;
    this.selectedEffect.set(option);
    this.emitSettings();
  }

  updatePositionX(value: string): void {
    this.positionX.set(Number(value));
    this.emitSettings();
  }

  updateOverlay(value: string): void {
    this.overlay.set(Number(value));
    this.emitSettings();
  }

  updateBlur(value: string): void {
    this.blur.set(Number(value));
    this.emitSettings();
  }

  togglePanel(): void {
    this.isOpen.update(isOpen => !isOpen);
  }

  reset(): void {
    this.selectedImage.set(this.imageOptions[0]);
    this.selectedSize.set(this.sizeOptions[0]);
    this.selectedEffect.set(this.effectOptions[4]);
    this.positionX.set(50);
    this.overlay.set(14);
    this.blur.set(0);
    this.emitSettings();
  }

  private emitSettings(): void {
    this.settingsChange.emit(this.settings());
  }

  private applyRandomPreset(): void {
    const preset = this.randomPresets[Math.floor(Math.random() * this.randomPresets.length)];
    const imageOption = this.imageOptions.find(option => option.value === preset.image);

    if (imageOption) {
      this.selectedImage.set(imageOption);
    }

    this.positionX.set(preset.positionX);
    this.blur.set(preset.blur ?? 0);
    this.emitSettings();
  }

  private getImageDefaultBlur(image: string): number {
    return image === 'assets/mcp-guide/hero-background2.png' ? 8 : 0;
  }
}
